import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";

/**
 * Track the current session locally so role queries only fire once the
 * session is restored from storage. Using getSession() avoids the
 * /auth/v1/user network call (which Safari was hammering in a loop).
 */
const useSessionReady = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      setIsReady(true);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      setIsReady(true);
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  return { session, isReady };
};

export const useUserRole = () => {
  const { session, isReady } = useSessionReady();
  const userId = session?.user?.id ?? null;

  return useQuery({
    queryKey: ["user-roles", userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);

      if (error) {
        console.error("Error fetching user roles:", error);
        throw error;
      }
      return data?.map(r => r.role) || [];
    },
    // Only run once the session has been restored AND we actually have a user.
    enabled: isReady && !!userId,
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

export const useIsAdmin = () => {
  const { data: roles, isLoading } = useUserRole();
  return {
    isAdmin: roles?.includes("admin") || false,
    isLoading,
  };
};

export const useIsConsultant = () => {
  const { data: roles, isLoading } = useUserRole();
  return {
    isConsultant: roles?.includes("consultant") || false,
    isLoading,
  };
};

export const useIsAdminOrConsultant = () => {
  const { data: roles, isLoading } = useUserRole();
  return {
    isAdminOrConsultant: roles?.some(role => role === "admin" || role === "consultant") || false,
    isLoading,
  };
};

export const useIsMarketingAndSales = () => {
  const { data: roles, isLoading } = useUserRole();
  return {
    isMarketingAndSales: roles?.includes("marketing_and_sales") || false,
    isLoading,
  };
};

export const useIsFinance = () => {
  const { data: roles, isLoading } = useUserRole();
  return {
    isFinance: roles?.includes("finance") || false,
    isLoading,
  };
};

export const useIsReporting = () => {
  const { data: roles, isLoading } = useUserRole();
  return {
    isReporting: roles?.includes("reporting") || false,
    isLoading,
  };
};
